import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';


import { queryClient, keepPreviousData } from '../queryClient';
import { QueryServerKeys } from 'constants/QueryServerKeys';
import { recheckAccess } from 'hooks/useAccessSync';
import { getAzurePlans, getToggleFeatures } from 'services/toggleFeature';
import {
  addUserToFeature,
  getAccountTypes,
  getUserRoleById,
  getUserRoles,
  getUsers,
  getDepartments,
  me,
  updateUser,
  getCliPermissions,
} from 'services/user';
import { displaySuccessMessage, displayErrorMessage } from 'utils';
import { getErrorMessage, showErrorMessage } from 'utils/errors';
import { toggleFeatures } from 'types/toggleFeatures';
import {
  getClusterByName,
  getClusters,
  updateCluster,
  updateAutoScalingConfig,
  syncPythonPackages,
  getRestoredClusters,
  getInstanceSchema,
  simpleClusterSearchWithAttribute,
} from 'services/cluster';
import {
  createConfiguration,
  getConfigurations,
  getCostType,
  getSlaType,
  updateConfiguration,
} from 'services/configuration';
import { ImagesResult, Instance, InstanceMetadataResponse } from 'types/cluster';
import { Department, getUserRolesResponse } from 'types/user';
import {
  disableChidoriPublicApi,
  exposeChidoriCredentials,
  fetchInstanceChidoriStatus,
  getChidoriSizes,
  modifiedChidoriSize,
  updateChidoriSizes,
  upgradeChidori,
  restartChidori,
  setChidoriMemoryLimit,
  getChidoriStatus,
} from 'services/sparkmaster';
import {
  getCmcSizes,
  updateCmcSizes,
  getCmcSizesForInstance,
} from 'services/cmc';
import { CmcSizeUpdateBody } from 'types/cmc';
import {
  addGlobalNotification,
  getNotificationsInstanceUsers,
} from 'services/notifications';
import { NotificationInstanceUsersData } from 'types/notification';
import { getOperators } from 'services/contextManager';
import { getImages, getIncortaVersions } from 'services/images';
import { getFeatureBits } from 'services/featureBit';
import {
  getNotebookUsersConfigs,
  updateNotebookUsersConfigs,
  updateNotebook,
} from 'services/notebook';
import { NotebookUsersConfig } from 'types/notebook';
import { SLATypeData } from 'types/slatype';
import { OperatorsData } from 'types/operators';

export function useGetUserData({
  isAuthenticated,
}: {
  isAuthenticated: boolean;
}) {
  const queryOptions = useQuery({
    queryKey: [QueryServerKeys.USER.GET_USER_DATA],
    queryFn: () => me(),
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
  return queryOptions;
}

export function useUpdateUser(uid) {
  const queryOptions = useMutation({
    mutationFn: async ({ uid, user }: { uid: string; user: any }) => {
      return await updateUser(uid, user);
    },
    mutationKey: [QueryServerKeys.USER.UPDATE_USER, uid],
      onSuccess(result) {
        [
          QueryServerKeys.USER_ROLES.GET_USER_ROLES,
          QueryServerKeys.ADMIN_ACCESS.USER_ACCESS,
        ].forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
        recheckAccess();
        displaySuccessMessage(result.data?.message);
      },
      onError(error: AxiosError) {
        // queryClient.invalidateQueries({ queryKey: //   QueryServerKeys.USER_ROLES.GET_USER_ROLE_BY_ID,
        // });
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

export function useGetAzurePlans({
  userId,
  userRole,
}: {
  userId?: string;
  userRole?: string;
}) {
  return useQuery({
    queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.AZURE_PLANS, userId],
    queryFn: async () => {
      const result = await getAzurePlans({ userId, userRole });
      return result?.data?.body;
    },
    enabled: !!userId,
  });
}

export function useAddUserToAzureSupportFeature() {
  const queryOptions = useMutation({
    mutationFn: async ({
      uid,
      tackle_plan,
      userRole,
    }: {
      uid: string;
      tackle_plan: string;
      userRole: string;
    }) => {
      const result = await addUserToFeature({
        feature: toggleFeatures.azureSupport,
        uid,
        tackle_plan,
        userRole,
      });
      return result;
    },
    mutationKey: [QueryServerKeys.SYSTEM_MANAGEMENT.ADD_USER_TO_FEATURE],
      onSuccess(result) {
        queryClient.invalidateQueries({ queryKey: [QueryServerKeys.USER.GET_USERS] });
        displaySuccessMessage(result.data?.message);
      },
      onError(error: any) {
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

export function getPaginatedData({
  key,
  page,
  search,
  limit,
  service,
  options,
  scope,
}: {
  key: string;
  page: number;
  search: string;
  service: Function;
  limit?: number;
  options?: Object;
  scope?: string;
}) {
  const queryKey = [key, page, search, ...[limit], ...(scope ? [scope] : [])];
  const queryFn = async () => {
    const res = await service({ page, search, limit });
    if (res === null) {
      return queryClient.getQueryData(queryKey);
    }
    return res;
  };
  const queryOptions = {
    staleTime: 60 * 1000, // 1 min
    ...options,
  };
  return { queryKey, queryFn, queryOptions };
}

export function useGetUserRoles({
  page = 1,
  search = '',
  limit,
}: {
  page?: number;
  search?: string;
  limit?: number;
}) {
  const { queryKey, queryFn, queryOptions } = getPaginatedData({
    key: QueryServerKeys.USER_ROLES.GET_USER_ROLES,
    page,
    search,
    limit,
    service: getUserRoles,
  });
  return useQuery<getUserRolesResponse>({
    queryKey: queryKey,
    queryFn: queryFn,
    ...(queryOptions),
  });
}

export function useGetUserAccountTypes() {
  return useQuery({
    queryKey: [QueryServerKeys.ACCOUNT_TYPES.GET_USER_ACCOUNT_TYPES],
    queryFn: async () => {
      const result = await getAccountTypes();
      return result?.data?.roles;
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
  });
}

export function useGetUserRoleById({ roleId }: { roleId: number }) {
  return useQuery({
    queryKey: [QueryServerKeys.USER_ROLES.GET_USER_ROLE_BY_ID, roleId],
    queryFn: async () => {
      const result = await getUserRoleById({ roleId: roleId?.toString() });
      return result;
    },
    enabled: !!roleId,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
  });
}

export function useGetUsers({
  page = 1,
  search = '',
  limit,
}: {
  page?: number;
  search?: string;
  limit?: number;
}) {
  const { queryKey, queryFn, queryOptions } = getPaginatedData({
    key: QueryServerKeys.USER.GET_USERS,
    page,
    search,
    limit,
    service: getUsers,
  });
  return useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    ...(queryOptions),
  });
}

// Base helper to avoid repeating logic
export const useDepartmentsLookup = (queryKey, type) => {
  return useQuery<{ label: string; value: string }[]>({
    queryKey: [queryKey, type],
    queryFn: async () => {
      const res = await getDepartments(type);
      if (res === null) {
        return queryClient.getQueryData<{ label: string; value: string }[]>([queryKey, type]) ?? [];
      }
      if (Array.isArray(res)) {
        return res.map(item => ({
          label: item, 
          value: item 
        }));
      }
      return []
    },
    refetchOnWindowFocus: false,
  });
};

export const useDepartments = () => useDepartmentsLookup(QueryServerKeys.USER.GET_DEPARTMENTS, "name");

export const useTeams = () => useDepartmentsLookup(QueryServerKeys.USER.GET_TEAMS, "team");

export function useDepartmentRecords() {
  return useQuery<Department[]>({
    queryKey: [QueryServerKeys.USER.GET_DEPARTMENT_RECORDS],
    queryFn: async () => {
      const res = await getDepartments('', '', true);
      return Array.isArray(res) ? res : [];
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export const useClusterSchema = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<InstanceMetadataResponse[]>({
    queryKey: [QueryServerKeys.INSTANCE.GET_INSTANCE_SCHEMA],
    queryFn: async () => {
      const res = await getInstanceSchema();
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,

  });
};

export function useGetChidori({ instanceid = '' }: { instanceid: string }) {
  return useQuery({
    queryKey: [QueryServerKeys.CHIDORI.CHIDORI, instanceid],
    queryFn: () => fetchInstanceChidoriStatus(instanceid),
    staleTime: 0,
  });
}

export function useExposeChidoriCredentials({
  instanceName,
}: {
  instanceName: string;
}) {
  const queryOptions = useMutation({
    mutationFn: async () => {
      const res = await exposeChidoriCredentials(instanceName);
      return res;
    },
    mutationKey: [
        QueryServerKeys.CHIDORI.EXPOSE_CHIDORI_CREDENTIALS,
        instanceName,
      ],
      async onSuccess() {
        displaySuccessMessage('Chidori API token generated.');
        await queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CHIDORI.CHIDORI] });
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          showErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

export function useDisableChidoriPublicApi({
  instanceName,
}: {
  instanceName: string;
}) {
  const queryOptions = useMutation({
    mutationFn: async () => {
      const res = await disableChidoriPublicApi(instanceName);
      return res;
    },
    mutationKey: [
        QueryServerKeys.CHIDORI.DISABLE_CHIDORI_PUBLIC_API,
        instanceName,
      ],
      async onSuccess() {
        displaySuccessMessage('Chidori API disabled.');
        await queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CHIDORI.CHIDORI] });
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          showErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

export function useGetChidoriSizes({
  page = 1,
  search = '',
}: {
  page?: number;
  search?: string;
}) {
  const { queryKey, queryFn, queryOptions } = getPaginatedData({
    key: QueryServerKeys.CHIDORI.GET_SIZES,
    page,
    search,
    service: getChidoriSizes,
  });
  return useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    ...(queryOptions),
  });
}

export function useUpdateChidoriSizes() {
  const queryOptions = useMutation({
    mutationFn: async ({ chidoriSizes }: { chidoriSizes: modifiedChidoriSize[] }) => {
      const result = await updateChidoriSizes({
        chidoriSizes,
      });
      return result;
    },
    mutationKey: [QueryServerKeys.CHIDORI.UPDATE_SIZES],
      onSuccess(result) {
        queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CHIDORI.GET_SIZES] });
        displaySuccessMessage(result.data?.message);
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}
export function useGetClusterByName({ name }: { name: string }) {
  return useQuery({
    queryKey: [QueryServerKeys.CLUSTER_MANAGEMENT.CLUSTER_BY_NAME, name],
    queryFn: async () => {
      const result = await getClusterByName(name);
      return result?.data;
    },
    enabled: !!name,
  });
}

export function useGetCmcSizes({ page = 1 }: { page?: number } = {}) {
  return useQuery({
    queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.CMC_SIZES, page],
    queryFn: async () => {
      try {
        const result = await getCmcSizes({ page });
        return result;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

  });
}

export function useUpdateCmcSizes() {
  return useMutation({
    mutationFn: async (cmcSizesData: CmcSizeUpdateBody[]) => {
      try {
        const result = await updateCmcSizes(cmcSizesData);
        return result;
      } catch (error) {
        throw error;
      }
    },
    onSuccess() {
        displaySuccessMessage('CMC Sizes updated successfully');
        queryClient.invalidateQueries({ queryKey: [
          QueryServerKeys.CAPACITY_MANAGEMENT.CMC_SIZES,
        ] });
      },
      onError(error: AxiosError) {
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });
}
export function useGetCmcSizesForInstance({
  instanceID,
}: {
  instanceID: string;
}) {
  return useQuery({
    queryKey: [QueryServerKeys.CMC.GET_CMC_SIZES_FOR_INSTANCE, instanceID],
    queryFn: async () => {
      try {
        const result = await getCmcSizesForInstance({ instanceID });
        return result;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!instanceID,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

  });
}

export function useGetClusters({
  page = 1,
  search = '',
  limit = 10,
  from,
  keepPrevious = true,
}: {
  page?: number;
  search?: string;
  limit?: number;
  from?: string;
  keepPrevious?: boolean;
}) {
  const { queryKey, queryFn, queryOptions } = getPaginatedData({
    key: QueryServerKeys.CLUSTER_MANAGEMENT.CLUSTERS,
    page,
    search,
    limit,
    scope: from,
    service: (args: any) => getClusters({ ...args, from }),
    options: {
      staleTime: 15 * 1000,
      placeholderData: keepPrevious ? keepPreviousData : undefined,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: (query: { state: { data?: { data?: { instances?: Instance[] } } } }) => {
        const instances = query.state.data?.data?.instances ?? [];
        const stillChanging = instances.some(
          instance =>
            instance.status === 'loading' ||
            (instance.status === 'running' &&
              !!instance.instanceServices?.[0] &&
              instance.instanceServices[0].analytics_status !== 'running'),
        );
        return stillChanging ? 5 * 1000 : 30 * 1000;
      },
    },
  });
  return useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    ...(queryOptions),
  });
}

const CLUSTER_LOOKUP_LIMIT = 10;

export function useGetLiveInstance(snapshot: Instance) {
  const { data } = useGetClusters({
    search: snapshot.name,
    limit: CLUSTER_LOOKUP_LIMIT,
    keepPrevious: false,
  });
  return useMemo(
    () =>
      data?.data?.instances?.find(
        (candidate: Instance) => candidate.id === snapshot.id,
      ),
    [data, snapshot.id],
  );
}

export function useFetchClusterByName() {
  return useCallback(async (name: string) => {
    const response = await queryClient.fetchQuery({
      queryKey: [
        QueryServerKeys.CLUSTER_MANAGEMENT.CLUSTERS,
        1,
        name,
        CLUSTER_LOOKUP_LIMIT,
      ],
      queryFn: () =>
        getClusters({ page: 1, search: name, limit: CLUSTER_LOOKUP_LIMIT }),
      staleTime: 15 * 1000,
    });
    const target = name.toLowerCase();
    return (
      response?.data?.instances?.find(
        (candidate: Instance) => candidate.name.toLowerCase() === target,
      ) ?? null
    );
  }, []);
}

export function useSuggestClusterNames() {
  return useCallback(async (query: string) => {
    const res = await simpleClusterSearchWithAttribute<{
      id: string;
      name: string;
    }>({ attribute: 'name', search: query });
    return (res.data?.instances ?? []).map(cluster => cluster.name);
  }, []);
}

export function useUpdateCluster() {
  return useMutation<
    AxiosResponse,
    AxiosError,
    { name: string; cluster: Instance }
  >({
    mutationFn: async ({ name, cluster }) => {
      const res = await updateCluster(name, cluster);
      return res;
    },
    mutationKey: [QueryServerKeys.CLUSTER_MANAGEMENT.UPDATE_CLUSTER],
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CLUSTER_MANAGEMENT.CLUSTERS] });
      displaySuccessMessage(result.data.message);
    },
    onError: error => {
      displayErrorMessage(getErrorMessage(error));
    },
  });
}

export function useGetConfigurations({
  page = 1,
  search = '',
}: {
  page?: number;
  search?: string;
}) {
  const { queryKey, queryFn, queryOptions } = getPaginatedData({
    key: QueryServerKeys.SYSTEM_MANAGEMENT.CONFIGURATIONS,
    page,
    search,
    service: getConfigurations,
  });
  return useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    ...(queryOptions),
  });
}

export function useGetToggleFeatures({
  page = 1,
  search = '',
}: {
  page?: number;
  search?: string;
}) {
  const { queryKey, queryFn, queryOptions } = getPaginatedData({
    key: QueryServerKeys.SYSTEM_MANAGEMENT.TOGGLE_FEATURES,
    page,
    search,
    service: getToggleFeatures,
  });
  return useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    ...(queryOptions),
  });
}

export function useGetUserPermissions({
  handleSuccess,
  handleError,
}: {
  handleSuccess?: Function;
  handleError?: Function;
}) {
  return useQuery({
    queryKey: [QueryServerKeys.USER.PERMISSIONS],
    queryFn: getCliPermissions,
    refetchOnWindowFocus: false,
  });
}

export function useCreateConfiguration() {
  const queryOptions = useMutation({
    mutationFn: async ({ configuration }: { configuration: any }) => {
      const result = await createConfiguration(configuration);
      return result;
    },
    mutationKey: [QueryServerKeys.SYSTEM_MANAGEMENT.CREATE_CONFIGURATIONS],
      onSuccess(result) {
        queryClient.invalidateQueries({ queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.CONFIGURATIONS] });
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });
  return queryOptions;
}

export function useUpdateConfiguration() {
  const queryOptions = useMutation({
    mutationFn: async ({ id, configuration }: { id: any; configuration: any }) => {
      const result = await updateConfiguration(id, configuration);
      return result;
    },
    mutationKey: [QueryServerKeys.SYSTEM_MANAGEMENT.UPDATE_CONFIGURATIONS],
      onSuccess(result) {
        queryClient.invalidateQueries({ queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.CONFIGURATIONS] });
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });
  return queryOptions;
}

export function useUpdateAutoScalingConfig() {
  const queryOptions = useMutation({
    mutationFn: async ({
      instanceID,
      instanceName,
      requestDelay,
      coolDelay,
      rejectedQueriesUp,
      rejectedQueriesDown,
      consecutiveQueries,
    }: {
      instanceID: string;
      instanceName: string;
      requestDelay: number;
      coolDelay: number;
      rejectedQueriesUp: number;
      rejectedQueriesDown: number;
      consecutiveQueries: number;
    }) => {
      const result = await updateAutoScalingConfig({
        instanceID,
        instanceName,
        requestDelay,
        coolDelay,
        rejectedQueriesUp,
        rejectedQueriesDown,
        consecutiveQueries,
      });
      return result;
    },
    mutationKey: [QueryServerKeys.CLUSTER_MANAGEMENT.UPDATE_AUTO_SCALING],
      onSuccess(result) {
        queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CLUSTER_MANAGEMENT.CLUSTERS] });
        displaySuccessMessage(result.data?.body?.message);
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });
  return queryOptions;
}

export function useSyncPythonPackages({ instanceID }: { instanceID: string }) {
  const queryOptions = useMutation({
    mutationFn: async () => {
      const res = await syncPythonPackages({
        instanceID,
      });
      return res;
    },
    mutationKey: [
        QueryServerKeys.CLUSTER_MANAGEMENT.SYNC_PYTHON_PACKAGES,
        instanceID,
      ],
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CLUSTER_MANAGEMENT.CLUSTERS] });
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          showErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

export function useUpgradeChidori(userID) {
  const queryOptions = useMutation({
    mutationFn: async ({ instanceName, target }: { instanceName: string; target?: string }) => {
      const result = await upgradeChidori(instanceName, userID, target);

      return result;
    },
    mutationKey: [QueryServerKeys.CHIDORI.UPGRADE, userID],
      onSuccess(result) {
        queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CHIDORI.CHIDORI] });
        displaySuccessMessage(result.data?.message);
      },
      onError(error: any) {
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

// Polled only while a restart or upgrade is in flight (the caller passes the interval), so the
// card can show what the server is waiting on. `busy` asks for the busy probe, which costs an
// exec per chidori pod, so only while a drain is being followed. `operationKey` separates one
// operation's data from the last one's.
export function useChidoriStatus({
  instanceName,
  userID,
  operationKey = '',
  busy = false,
  enabled = true,
  refetchInterval = false,
}: {
  instanceName: string;
  userID: string;
  operationKey?: string;
  busy?: boolean;
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery({
    queryKey: [QueryServerKeys.CHIDORI.STATUS, instanceName, operationKey, busy],
    queryFn: async () => (await getChidoriStatus(instanceName, userID, busy)).data,
    enabled: !!instanceName && enabled,
    refetchInterval,
    // `busy` is part of the key: without carrying the last data across the flip, the render with
    // no data reads as "run not reported", which flips the flag back -- a loop of extra probes.
    placeholderData: keepPreviousData,
  });
}

export function useSetChidoriMemoryLimit(userID) {
  const queryOptions = useMutation({
    mutationFn: async ({ instanceName, memoryLimitGB }: { instanceName: string; memoryLimitGB: number }) => {
      const result = await setChidoriMemoryLimit(instanceName, userID, memoryLimitGB);

      return result;
    },
    mutationKey: [QueryServerKeys.CHIDORI.MEMORY, userID],
    onSuccess(result) {
      displaySuccessMessage(result.data?.message);
    },
    onError(error: any) {
      const errorMessage = error && getErrorMessage(error);
      if (errorMessage) {
        displayErrorMessage(errorMessage);
      }
    },
  });

  return queryOptions;
}

export function useRestartChidori(userID) {
  const queryOptions = useMutation({
    mutationFn: async ({ instanceName }: { instanceName: string }) => {
      const result = await restartChidori(instanceName, userID);

      return result;
    },
    mutationKey: [QueryServerKeys.CHIDORI.RESTART, userID],
      onSuccess(result) {
        displaySuccessMessage(result.data?.message);
      },
      onError(error: any) {
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

export function useNotificationsInstanceUsers({
  page = 1,
  search = '',
  limit,
}: {
  page?: number;
  search?: string;
  limit?: number;
}) {
  const { queryKey, queryFn, queryOptions } = getPaginatedData({
    key: QueryServerKeys.NOTIFICATION.INSTANCE_USERS,
    page,
    search,
    limit,
    service: getNotificationsInstanceUsers,
  });

  return useQuery<AxiosResponse<NotificationInstanceUsersData>>({
    queryKey: queryKey,
    queryFn: queryFn,
    ...queryOptions,
    staleTime: 2 * 1000,
    refetchInterval: 5 * (60 * 1000), // 5 mins
  });
}

export function useAddGlobalNotification({
  selectedUsersIDs,
}: {
  selectedUsersIDs: string[];
}) {
  const queryOptions = useMutation({
    mutationFn: async ({
      event,
      message,
      method,
      templateId,
      templateParameters,
      includeImpactedClusters,
      searchConditions,
      userInstanceData,
      usersIDs,
    }: {
      event?: string;
      message?: string;
      method: { email: boolean; alert: boolean };
      templateId?: string;
      templateParameters?: Record<string, string>;
      includeImpactedClusters?: boolean;
      searchConditions?: string;
      userInstanceData?: any;
      usersIDs?: string[];
    }) => {
      const res = await addGlobalNotification({
        event,
        message,
        usersIDs: usersIDs || selectedUsersIDs, // Use provided usersIDs or fallback to selectedUsersIDs
        method,
        templateId,
        templateParameters,
        includeImpactedClusters,
        searchConditions,
        userInstanceData,
      });
      return res;
    },
    mutationKey: [
        QueryServerKeys.NOTIFICATION.ADD_GLOBAL_NOTIFICATION,
        selectedUsersIDs,
      ],
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [QueryServerKeys.NOTIFICATION.INSTANCE_USERS] });
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          showErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

export function useOperators() {
  const queryOptions = useQuery({
    queryKey: [QueryServerKeys.OPERATORS.GET_OPERATORS],
    queryFn: async () => {
      const res = await getOperators();
      if (res.data.operators === null) {
        return queryClient.getQueryData<OperatorsData['operators']>([QueryServerKeys.OPERATORS.GET_OPERATORS]);
      }
      return res.data.operators;
    },
    refetchOnWindowFocus: false,
  });
  return queryOptions;
}

export function useUserRoles() {
  const queryOptions = useQuery({
    queryKey: [QueryServerKeys.USER_ROLES.GET_USER_ROLES],
    queryFn: async () => {
      const res = await getUserRoles({ search: '', fetchAll: true });
      const data = res.userRoles;
      return data;
    },
  });
  return queryOptions;
}

export function useIncortaVersions() { //only returns an array of strings
  const queryOptions = useQuery({
    queryKey: [QueryServerKeys.INCORTA_VERSIONS.GET_INCORTA_VERSIONS],
    queryFn: async () => {
      const res = await getImages();
      if (res.data.images === null) {
        return queryClient.getQueryData<ImagesResult['images']>([QueryServerKeys.INCORTA_VERSIONS.GET_INCORTA_VERSIONS]);
      }
      return res.data.images;
    },
    refetchOnWindowFocus: false,
  });
  return queryOptions;
}
export function useNewIncortaVersions() { //returns objects with tags 
  const queryOptions = useQuery({
    queryKey: [QueryServerKeys.INCORTA_VERSIONS.GET_INCORTA_VERSIONS],
    queryFn: async () => {
      const res = await getIncortaVersions();
      const data = res.images;
      return data;
    },
  });
  return queryOptions;
}
export function useFeatureBits() {
  const queryOptions = useQuery({
    queryKey: [QueryServerKeys.FEATURE_BITS.GET_FEATURE_BITS],
    queryFn: async () => {
      const res = await getFeatureBits({ search: '' });
      if (res.data.featureBits === null) {
        return queryClient.getQueryData([QueryServerKeys.FEATURE_BITS.GET_FEATURE_BITS]);
      }
      return res.data.featureBits;
    },
    refetchOnWindowFocus: false,
  });
  return queryOptions;
}

export function useCostTypes() {
  const queryOptions = useQuery({
    queryKey: [QueryServerKeys.COST_TYPE.GET_COST_TYPE],
    queryFn: async () => {
      const res = await getCostType();
      if (res.data.costTypes === null) {
        return queryClient.getQueryData([QueryServerKeys.COST_TYPE.GET_COST_TYPE]);
      }
      return res.data.costTypes;
    },
    refetchOnWindowFocus: false,
  });
  return queryOptions;
}

export function useSLAType() {
  const queryOptions = useQuery<SLATypeData['slaTypes']>({
    queryKey: [QueryServerKeys.SLA_TYPE.GET_SLA_TYPE],
    queryFn: async () => {
      const res = await getSlaType();
      if (res.data.slaTypes === null) {
        return queryClient.getQueryData<SLATypeData['slaTypes']>([QueryServerKeys.SLA_TYPE.GET_SLA_TYPE]);
      }
      return res.data.slaTypes;
    },
    refetchOnWindowFocus: false,
  });
  return queryOptions;
}

export function useGetNotebookUsersConfigs({
  handleSuccess,
  handleError,
  enabled = true,
}: {
  handleSuccess?: Function;
  handleError?: Function;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [QueryServerKeys.NOTEBOOK.GET_NOTEBOOK_USERS_CONFIGS],
    queryFn: async () => {
      const res = await getNotebookUsersConfigs();
      return res?.data?.allChidoriXSizesInTable?.rows;
    },
    enabled,


      refetchOnWindowFocus: false,
  });
}

export function useUpdateNotebookUsersConfigs() {
  const queryOptions = useMutation({
    mutationFn: async ({
      notebookUsersSizes,
    }: {
      notebookUsersSizes: NotebookUsersConfig[];
    }) => {
      const result = await updateNotebookUsersConfigs({
        configs: notebookUsersSizes,
      });
      return result;
    },
    mutationKey: [QueryServerKeys.NOTEBOOK.UPDATE_NOTEBOOK_USERS_CONFIGS],
      onSuccess(result) {
        queryClient.invalidateQueries({ queryKey: [QueryServerKeys.NOTEBOOK.GET_NOTEBOOK_USERS_CONFIGS] });
        displaySuccessMessage(result.data?.message);
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

export function useUpdateNotebook({ instanceID }: { instanceID: string }) {
  return useMutation({
    mutationFn: async ({
      enableBusinessUserNotebook,
      businessUserNotebookCount,
    }: {
      enableBusinessUserNotebook: boolean;
      businessUserNotebookCount?: number;
    }) => {
      return await updateNotebook({
        instanceID,
        enableBusinessUserNotebook,
        businessUserNotebookCount,
      });
    },
    mutationKey: [QueryServerKeys.NOTEBOOK.UPDATE_NOTEBOOK, instanceID],
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CLUSTER_MANAGEMENT.CLUSTERS] });
      },
      onError(error: any) {
        let errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          showErrorMessage(errorMessage);
        }
      },
  });
}

export function useGetRestoredClusters({
  page = 1,
  search = '',
  limit = 10,
}: {
  page?: number;
  search?: string;
  limit?: number;
}) {
  const { queryKey, queryFn, queryOptions } = getPaginatedData({
    key: QueryServerKeys.CLUSTER_MANAGEMENT.RESTORED_CLUSTERS,
    page,
    search,
    limit,
    service: getRestoredClusters,
  });
  return useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    ...(queryOptions),
  });
}
